import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Observable, Subject } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class WakeLockService implements OnDestroy {
    private wakeLock: WakeLockSentinel | null = null;
    private visibilityChangeHandler: () => void;
    private pageHideHandler: () => void;
    private pageshowHandler: () => void;
    private wakeLockReleasedHandler: () => void;
    private isAcquiring = false;
    private isReleasing = false;
    private initialized = false;

    private _release$ = new Subject<void>();
    public get release$(): Observable<void> { return this._release$.asObservable(); }

    constructor(private ngZone: NgZone) {
        this.visibilityChangeHandler = this.onVisibilityChange.bind(this);
        this.pageHideHandler = this.onPageHide.bind(this);
        this.pageshowHandler = this.onPageShow.bind(this);
        this.wakeLockReleasedHandler = this.onWakeLockReleased.bind(this);
    }

    public init() {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        document.addEventListener("visibilitychange", this.visibilityChangeHandler, false);
        document.addEventListener("pagehide", this.pageHideHandler, false);
        document.addEventListener("pageshow", this.pageshowHandler, false);
        this.acquire();
    }

    public ngOnDestroy(): void {
        document.removeEventListener("visibilitychange", this.visibilityChangeHandler, false);
        document.removeEventListener("pagehide", this.pageHideHandler, false);
        document.removeEventListener("pageshow", this.pageshowHandler, false);
        this.initialized = false;
        this._release$.complete();
        this._release$ = new Subject<void>();
        this.release();
    }

    public get isAcquired(): boolean {
        return this.wakeLock !== null;
    }

    private async onVisibilityChange(): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => {
            if (document.visibilityState === "visible") {
                await this.acquire();
            } else {
                await this.release();
            }
        });
    }

    private async onPageHide(): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => {
            await this.release();
        });
    }

    private async onPageShow(): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => {
            await this.acquire();
        });
    }

    private async acquire(): Promise<void> {
        console.log("Attempting to acquire wake lock...");
        if (this.isAcquiring || this.wakeLock || !("wakeLock" in navigator)) {
            return;
        }

        // Ensure we only acquire when the page is visible
        if (document.visibilityState !== "visible") {
            return;
        }

        this.isAcquiring = true;
        try {
            this.wakeLock = await navigator.wakeLock.request("screen");
            if (this.wakeLock) {
                this.wakeLock.addEventListener("release", this.wakeLockReleasedHandler);
            }
        } catch (err) {
            console.warn("Failed to acquire wake lock:", err);
            this.wakeLock = null;
        } finally {
            this.isAcquiring = false;
        }
    }

    private async release(): Promise<void> {
        console.log("Attempting to release wake lock...");
        if (this.isReleasing || !this.wakeLock) {
            return;
        }

        this.isReleasing = true;
        try {
            this.wakeLock.removeEventListener("release", this.wakeLockReleasedHandler);
            await this.wakeLock.release();
        } catch (err) {
            console.warn("Failed to release wake lock:", err);
        } finally {
            this.wakeLock = null;
            this.isAcquiring = false;
            this.isReleasing = false;
        }
    }

    private async onWakeLockReleased(): Promise<void> {
        await this.ngZone.runOutsideAngular(async () => {
            this.wakeLock = null;
            this.isAcquiring = false;
            this._release$.next();
        });
    }
}
